import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel } from 'primereact/carousel';
import { Button } from 'primereact/button';
import { EcommerceCategoriaService } from '../services/EcommerceCategoriaService';
import { EcommerceProductoService } from '../services/EcommerceProductoService';

/**
 * Genera un gradient aleatorio pero consistente basado en un ID
 */
const generateGradient = (id) => {
    const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',      // Púrpura
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',      // Rosa-Rojo
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',      // Azul-Cyan
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',      // Verde-Aqua
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',      // Coral-Amarillo
        'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',      // Cyan-Morado
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',      // Agua-Rosado
        'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',      // Naranja-Rojo
    ];
    return gradients[id % gradients.length];
};

/**
 * Genera un banner para una categoría
 */
const generateCategoryBanner = (category, productCount) => {
    const messages = [
        `Explora nuestros ${productCount} ${category.nombre}`,
        `Los mejores ${category.nombre} están aquí`,
        `Descubre ${category.nombre} exclusivos`,
        `${category.nombre} con los mejores precios`,
    ];

    return {
        id: `cat-${category.id}`,
        title: category.nombre,
        subtitle: messages[category.id % messages.length],
        discount: `${productCount} productos disponibles`,
        cta: 'Ver categoría',
        gradient: generateGradient(category.id),
        category: category.id,
        type: 'category'
    };
};

/**
 * Genera un banner para el producto más barato
 */
const generateCheapestProductBanner = (products) => {
    if (!products || products.length === 0) return null;

    const cheapest = products.reduce((prev, current) => 
        (prev.precioVenta < current.precioVenta) ? prev : current
    );

    return {
        id: 'cheapest',
        title: '¡Ofertas Increíbles!',
        subtitle: `Encuentra ${cheapest.nombre} desde`,
        discount: `$${cheapest.precioVenta.toFixed(2)}`,
        cta: 'Ver oferta',
        gradient: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
        category: cheapest.idCategoria,
        type: 'product'
    };
};

/**
 * Genera un banner para nuevos productos
 */
const generateNewProductsBanner = (products) => {
    if (!products || products.length === 0) return null;

    const newCount = products.slice(0, 5).length;

    return {
        id: 'new',
        title: '✨ Productos Nuevos',
        subtitle: 'Descubre lo último en nuestro catálogo',
        discount: `${newCount} productos nuevos`,
        cta: 'Explorar',
        gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        category: null,
        type: 'new'
    };
};

const BannerCarousel = () => {
    const navigate = useNavigate();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const idEmpresa = 1;

    // Cargar datos y generar banners dinámicos
    useEffect(() => {
        const loadBanners = async () => {
            try {
                setLoading(true);

                // Obtener categorías y productos del backend
                const [categoriesResponse, productsResponse] = await Promise.all([
                    EcommerceCategoriaService.obtenerCategorias(idEmpresa),
                    EcommerceProductoService.obtenerProductos(idEmpresa)
                ]);

                const categories = categoriesResponse || [];
                const products = productsResponse || [];

                // Generar banners dinámicos
                const generatedBanners = [];

                // Banner 1: Producto más barato
                const cheapestBanner = generateCheapestProductBanner(products);
                if (cheapestBanner) generatedBanners.push(cheapestBanner);

                // Banner 2: Productos nuevos
                const newBanner = generateNewProductsBanner(products);
                if (newBanner) generatedBanners.push(newBanner);

                // Banners 3+: Una por cada categoría
                categories.forEach(category => {
                    const categoryProducts = products.filter(p => p.idCategoria === category.id);
                    const banner = generateCategoryBanner(category, categoryProducts.length);
                    generatedBanners.push(banner);
                });

                // Si no hay banners generados, usar placeholder
                if (generatedBanners.length === 0) {
                    generatedBanners.push({
                        id: 'placeholder',
                        title: 'Bienvenido a Nuestro E-commerce',
                        subtitle: 'Estamos cargando nuestros productos',
                        discount: 'Próximamente disponible',
                        cta: 'Explorar',
                        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        category: null,
                        type: 'placeholder'
                    });
                }

                setBanners(generatedBanners);
            } catch (error) {
                console.error('Error cargando banners:', error);
                // Fallback a banner estático
                setBanners([{
                    id: 'fallback',
                    title: 'Bienvenido',
                    subtitle: 'Explora nuestros productos',
                    discount: 'Ofertas especiales',
                    cta: 'Ver más',
                    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    category: null,
                    type: 'fallback'
                }]);
            } finally {
                setLoading(false);
            }
        };

        loadBanners();
    }, []);

    const bannerTemplate = (banner) => {
        return (
            <div
                className="ec-banner-item"
                style={{ background: banner.gradient }}
            >
                <div className="ec-banner-content">
                    <p className="ec-banner-subtitle">{banner.subtitle}</p>
                    <h1 className="ec-banner-title">{banner.title}</h1>
                    <p className="ec-banner-discount">{banner.discount}</p>
                    <Button
                        label={banner.cta}
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        className="ec-banner-cta"
                        onClick={() => {
                            if (banner.category) {
                                navigate(`/tienda/catalogo?cat=${banner.category}`);
                            } else {
                                navigate('/tienda/catalogo');
                            }
                        }}
                    />
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="ec-banner ec-animate-fade">
                <div className="ec-banner-item" style={{ background: 'linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%)', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ color: '#999' }}>Cargando promociones...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="ec-banner ec-animate-fade">
            <Carousel
                value={banners}
                itemTemplate={bannerTemplate}
                numVisible={1}
                numScroll={1}
                circular
                autoplayInterval={5000}
                showNavigators={true}
                showIndicators={true}
            />
        </div>
    );
};

export default BannerCarousel;
